import React, { memo } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import Svg, { Path } from "react-native-svg";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { router } from "expo-router";
import { useBrandLogo } from "@/hooks/useBrandLogo";
import { UserFinanceDataResponse, AccountSummary } from "@/types/financeData";
import { SubscriptionSetupType } from "@/app/my-money/setup";

interface PaychecksCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Helper to format currency from cents
const formatCurrency = (cents: number, showSign = false): string => {
  const dollars = cents / 100;
  const formatted = dollars.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (showSign && cents > 0) {
    return `+$${formatted}`;
  }
  if (showSign && cents < 0) {
    return `-$${Math.abs(dollars).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }
  return `$${formatted}`;
};

// Format relative date
const formatRelativeDate = (dateString?: string): string => {
  if (!dateString) return "Yesterday";
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Get initial letter for fallback logo
const getInitialLetter = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

// Plus icon component
const PlusIcon = () => (
  <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <Path
      d="M6 2V10M2 6H10"
      stroke="#0F9129"
      strokeWidth={1.5}
      strokeLinecap="round"
    />
  </Svg>
);

// Capitalize first letter of each word
const toSentenceCase = (str: string): string =>
  str.replace(/\b\w/g, (c) => c.toUpperCase());

// Get account type label
const getAccountTypeLabel = (account: AccountSummary): string => {
  if (account.type === "depository") {
    return "Payroll";
  }
  return toSentenceCase(account.subtype || "monthly");
};

// Account paycheck item component
const PaycheckItem = ({
  account,
  amount,
  isLast,
}: {
  account: AccountSummary;
  amount: number;
  isLast: boolean;
}) => {
  const { icon } = useBrandLogo(account.name);

  return (
    <View>
      <View style={styles.itemRow}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          {icon ? (
            <Image source={{ uri: icon }} style={styles.logo} />
          ) : (
            <View style={styles.logoFallback}>
              <Text style={styles.logoInitial}>
                {getInitialLetter(account.name)}
              </Text>
            </View>
          )}
        </View>

        {/* Name and type */}
        <View style={styles.itemInfo}>
          <Text style={styles.itemName} numberOfLines={1}>
            {account.name}
          </Text>
          <Text style={styles.itemSubtext}>
            {getAccountTypeLabel(account)} -{" "}
            {formatRelativeDate(account.lastSyncedAt)}
          </Text>
        </View>

        {/* Amount */}
        <Text style={styles.amountGreen}>{formatCurrency(amount, true)}</Text>
      </View>

      {!isLast && <View style={styles.divider} />}
    </View>
  );
};

export default memo(function PaychecksCard({
  financeData,
}: PaychecksCardProps) {
  const accounts = financeData?.accounts || [];

  // Get income per account by summing sources that belong to this account
  const getAccountIncome = (account: AccountSummary): number => {
    const sources = financeData?.analysis?.income?.sources || [];
    // If sources have accountId (new format), match by account
    const hasAccountIds = sources.some((s) => s.accountId);
    if (hasAccountIds) {
      const accountSources = sources.filter(
        (s) => s.accountId === account.accountId
      );
      return accountSources.reduce((sum, s) => sum + s.amount, 0);
    }
    // Fallback for old data without accountId: divide total equally
    const totalIncome = financeData?.analysis?.income?.totalMonthly || 0;
    return accounts.length > 0 ? Math.floor(totalIncome / accounts.length) : 0;
  };

  const handleLinkAccounts = () => {
    router.push({
      pathname: "/my-money/setup",
      params: { type: SubscriptionSetupType.MyMoney },
    });
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>Paychecks</Text>
        <Text style={styles.cardDescription}>
          Your salary and recurring income deposits.
        </Text>
      </View>

      <View style={styles.headerDivider} />

      {/* Paycheck list */}
      <View style={styles.itemsList}>
        {accounts.length > 0 ? (
          accounts.map((account, index) => (
            <PaycheckItem
              key={account.accountId + index}
              account={account}
              amount={getAccountIncome(account)}
              isLast={index === accounts.length - 1}
            />
          ))
        ) : (
          <Text style={styles.emptyText}>No linked accounts</Text>
        )}
      </View>

      {/* Link accounts button */}
      <TouchableOpacity
        onPress={handleLinkAccounts}
        style={styles.linkAccountsButton}
      >
        <View style={styles.plusIconContainer}>
          <PlusIcon />
        </View>
        <Text style={styles.linkAccountsText}>Link your accounts</Text>
      </TouchableOpacity>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 8,
    ...Shadows.card,
  },
  cardHeader: {
    gap: 4,
    paddingBottom: 8,
  },
  cardTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 23,
  },
  cardDescription: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 16,
  },
  headerDivider: {
    height: 1,
    backgroundColor: "#EBEBEB",
    marginBottom: 8,
  },
  itemsList: {
    gap: 0,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 12,
  },
  logoContainer: {
    width: 40,
    height: 40,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
  },
  logoInitial: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  itemInfo: {
    flex: 1,
    gap: 2,
  },
  itemName: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 27,
  },
  itemSubtext: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
    lineHeight: 23,
  },
  amountGreen: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
    lineHeight: 27,
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  linkAccountsButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 12,
  },
  plusIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#F3F7F3",
    alignItems: "center",
    justifyContent: "center",
  },
  linkAccountsText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
    lineHeight: 23,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    textAlign: "center",
    paddingVertical: 16,
  },
});
